import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, Trash2, Upload, User, GripVertical } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from "@/components/ui/dialog";

interface TeamMember {
  id: string;
  name: string;
  title: string;
  photo_url: string | null;
  display_order: number;
  is_active: boolean;
}

const TeamManager = () => {
  const queryClient = useQueryClient();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [newMember, setNewMember] = useState({ name: "", title: "" });
  const [uploadingId, setUploadingId] = useState<string | null>(null);

  const { data: teamMembers, isLoading } = useQuery({
    queryKey: ["admin-team-members"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("team_members")
        .select("*")
        .order("display_order", { ascending: true });

      if (error) throw error;
      return data as TeamMember[];
    },
  });

  const addMemberMutation = useMutation({
    mutationFn: async (member: { name: string; title: string }) => {
      const maxOrder = teamMembers?.reduce(
        (max, m) => Math.max(max, m.display_order),
        0
      ) ?? 0;

      const { data, error } = await supabase
        .from("team_members")
        .insert({
          name: member.name,
          title: member.title,
          display_order: maxOrder + 1,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-team-members"] });
      queryClient.invalidateQueries({ queryKey: ["team-members"] });
      setIsAddDialogOpen(false);
      setNewMember({ name: "", title: "" });
      toast.success("Team member added!");
    },
    onError: (error) => {
      toast.error("Failed to add team member: " + error.message);
    },
  });

  const updateMemberMutation = useMutation({
    mutationFn: async ({
      id,
      updates,
    }: {
      id: string;
      updates: Partial<TeamMember>;
    }) => {
      const { error } = await supabase
        .from("team_members")
        .update(updates)
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-team-members"] });
      queryClient.invalidateQueries({ queryKey: ["team-members"] });
    },
    onError: (error) => {
      toast.error("Failed to update: " + error.message);
    },
  });

  const deleteMemberMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("team_members")
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-team-members"] });
      queryClient.invalidateQueries({ queryKey: ["team-members"] });
      toast.success("Team member deleted");
    },
    onError: (error) => {
      toast.error("Failed to delete: " + error.message);
    },
  });

  const handlePhotoUpload = async (
    memberId: string,
    file: File
  ) => {
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5MB");
      return;
    }

    setUploadingId(memberId);

    try {
      const fileExt = file.name.split(".").pop();
      const fileName = `${memberId}-${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from("team-photos")
        .upload(fileName, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from("team-photos")
        .getPublicUrl(fileName);

      await updateMemberMutation.mutateAsync({
        id: memberId,
        updates: { photo_url: urlData.publicUrl },
      });

      toast.success("Photo uploaded!");
    } catch (error: any) {
      toast.error("Upload failed: " + error.message);
    } finally {
      setUploadingId(null);
    }
  };

  if (isLoading) {
    return <div className="p-4">Loading team members...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Team Members</h2>
        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="w-4 h-4 mr-2" />
              Add Member
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Team Member</DialogTitle>
              <DialogDescription>
                Add a new member to display on the homepage team section.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div>
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  value={newMember.name}
                  onChange={(e) =>
                    setNewMember({ ...newMember, name: e.target.value })
                  }
                  placeholder="Enter name"
                />
              </div>
              <div>
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  value={newMember.title}
                  onChange={(e) =>
                    setNewMember({ ...newMember, title: e.target.value })
                  }
                  placeholder="e.g., Founder, CMO, CEO"
                />
              </div>
              <Button
                onClick={() => addMemberMutation.mutate(newMember)}
                disabled={!newMember.name || !newMember.title}
                className="w-full"
              >
                Add Member
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-4">
        {teamMembers?.map((member) => (
          <Card key={member.id}>
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className="w-20 h-20 rounded-lg overflow-hidden bg-muted flex-shrink-0">
                  {member.photo_url ? (
                    <img
                      src={member.photo_url}
                      alt={member.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <User className="w-8 h-8 text-muted-foreground/40" />
                    </div>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <Input
                    value={member.name}
                    onChange={(e) =>
                      updateMemberMutation.mutate({
                        id: member.id,
                        updates: { name: e.target.value },
                      })
                    }
                    className="font-semibold mb-2"
                    placeholder="Name"
                  />
                  <Input
                    value={member.title}
                    onChange={(e) =>
                      updateMemberMutation.mutate({
                        id: member.id,
                        updates: { title: e.target.value },
                      })
                    }
                    className="text-sm"
                    placeholder="Title"
                  />
                </div>

                <div className="flex flex-col items-center gap-2">
                  <div className="flex items-center gap-2">
                    <Label htmlFor={`active-${member.id}`} className="text-xs">
                      Active
                    </Label>
                    <Switch
                      id={`active-${member.id}`}
                      checked={member.is_active}
                      onCheckedChange={(checked) =>
                        updateMemberMutation.mutate({
                          id: member.id,
                          updates: { is_active: checked },
                        })
                      }
                    />
                  </div>

                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={uploadingId === member.id}
                      onClick={() => {
                        const input = document.createElement("input");
                        input.type = "file";
                        input.accept = "image/*";
                        input.onchange = (e) => {
                          const file = (e.target as HTMLInputElement)
                            .files?.[0];
                          if (file) handlePhotoUpload(member.id, file);
                        };
                        input.click();
                      }}
                    >
                      <Upload className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => deleteMemberMutation.mutate(member.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}

        {(!teamMembers || teamMembers.length === 0) && (
          <div className="text-center py-12 text-muted-foreground">
            No team members yet. Add your first team member above!
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamManager;
